export interface StationInfo {
  station: string;
  name: string;
  latitude: number;
  longitude: number;
  elevation: number;
}

// Helper function to fix UTF-8 double-encoding issues in station names
function fixStationNameEncoding(name: string): string {
  if (!name) return name;

  const originalName = name;

  // Skip if name looks normal (no suspicious characters)
  if (!name.includes('Ã') && !name.includes('Â')) {
    return name;
  }

  // First attempt: Handle specific common patterns
  const specificFixes: Record<string, string> = {
    'Ã°': 'İ', // Turkish İ - İstanbul, İncirlik
    'Ã ': 'Š', // Šiauliai (Ã + space)
    AÃŸ: 'Aş', // Turkish ş
    'Ã±': 'ñ', // Spanish ñ
    'Ã¤': 'ä', // German ä
    'Ã¶': 'ö', // German ö
    'Ã¼': 'ü', // German ü
    'Ã…': 'Å', // Nordic Å (both patterns)
    'Ã¥': 'å', // Nordic å
    'Ã¦': 'æ', // Nordic æ
    'Ã¸': 'ø', // Nordic ø
    'Ã©': 'é', // French é
    'Ã¡': 'á', // Spanish á
    'Ã³': 'ó', // Spanish ó
    Ãº: 'ú', // Spanish ú
    'Ã‚': 'Â', // Â (for cases like Santo Ângelo)
    'Ã–': 'Ö', // Ö (for cases like Östersund)
  };

  let fixed = name;
  for (const [pattern, replacement] of Object.entries(specificFixes)) {
    if (fixed.includes(pattern)) {
      fixed = fixed.replace(new RegExp(pattern, 'g'), replacement);
    }
  }

  // If we made changes, return the result
  if (fixed !== name) {
    return fixed;
  }

  // Second attempt: Try double UTF-8 encoding fix
  try {
    const bytes = new Uint8Array(name.split('').map((char) => char.charCodeAt(0)));
    const fixed = new TextDecoder('utf-8').decode(bytes);

    if (isBetterEncoding(fixed, name)) {
      return fixed;
    }
  } catch {
    // If decoding fails, continue to other methods
  }

  return originalName;
}

// Helper function to determine if a candidate encoding is better than the original
function isBetterEncoding(candidate: string, original: string): boolean {
  if (!candidate || candidate === original) {
    return false;
  }

  // Count suspicious character sequences
  const suspiciousPatterns = ['Ã', 'Â€', 'Â°', 'Â´', 'Â¨', 'Â­'];

  const originalSuspicious = suspiciousPatterns.reduce(
    (count, pattern) => count + (original.split(pattern).length - 1),
    0,
  );
  const candidateSuspicious = suspiciousPatterns.reduce(
    (count, pattern) => count + (candidate.split(pattern).length - 1),
    0,
  );

  // Candidate is better if it has fewer suspicious patterns
  if (candidateSuspicious < originalSuspicious) {
    return true;
  }

  // Check for common international characters (good signs)
  const commonIntlChars = /[àáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿ]/g;
  const originalIntl = (original.match(commonIntlChars) || []).length;
  const candidateIntl = (candidate.match(commonIntlChars) || []).length;

  return candidateIntl > originalIntl && candidateSuspicious <= originalSuspicious;
}

export interface StationDataPoint {
  station: string;
  name: string;
  time: string;
  latitude: number;
  longitude: number;
  elevation: number;
  air_temperature_at_height_level_2m?: number;
  dew_point_temperature_at_height_level_2m?: number;
  wind_speed_at_height_level_10m?: number;
  wind_direction_at_height_level_10m?: number;
  air_pressure_at_mean_sea_level?: number;
  surface_air_pressure?: number;
  precipitation_amount_sum_1h?: number;
  precipitation_amount_sum_3h?: number;
  precipitation_amount_sum_6h?: number;
  precipitation_amount_sum_12h?: number;
  precipitation_amount_sum_24h?: number;
  cloud_area_fraction_at_entire_atmosphere?: number;
}

export interface StationDataQuery {
  station_ids?: string[];
  start_time?: string;
  end_time?: string;
  variables?: string[];
  limit?: number;
}

export interface StationDataResponse {
  data: StationDataPoint[];
  total_records: number;
  query_info: StationDataQuery;
}

export interface StationListResponse {
  stations: StationInfo[];
  total_stations: number;
}

export interface ApiResponse<T> {
  data?: T;
  error?: string;
}

export interface GetStationsParams {
  recent_hours?: number;
  variables?: string[]; // e.g., ['air_temperature_at_height_level_2m', 'wind_speed_at_height_level_10m']
}

export class StationService {
  private baseUrl: string;
  private getToken: () => Promise<string>;

  constructor(baseUrl: string, getToken: () => Promise<string>) {
    this.baseUrl = baseUrl;
    this.getToken = getToken;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    try {
      const token = await this.getToken();

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      // Fix encoding issues in station names if needed
      if (data && data.stations) {
        data.stations = data.stations.map((station: StationInfo) => ({
          ...station,
          name: fixStationNameEncoding(station.name),
        }));
      }

      return { data };
    } catch (error) {
      console.error(`Station service error for ${endpoint}:`, error);
      return {
        error: error instanceof Error ? error.message : 'An unknown error occurred',
      };
    }
  }

  async getStations(params?: GetStationsParams): Promise<ApiResponse<StationListResponse>> {
    const searchParams = new URLSearchParams();
    if (params?.recent_hours !== undefined) {
      searchParams.append('recent_hours', String(params.recent_hours));
    }
    if (params?.variables && params.variables.length > 0) {
      params.variables.forEach((v) => searchParams.append('variables', v));
    }
    const query = searchParams.toString();
    const endpoint = `/v1/station-data/stations${query ? `?${query}` : ''}`;
    return this.makeRequest<StationListResponse>(endpoint);
  }

  async getStationData(params: StationDataQuery): Promise<ApiResponse<StationDataResponse>> {
    const searchParams = new URLSearchParams();

    if (params.station_ids && params.station_ids.length > 0) {
      params.station_ids.forEach((id) => searchParams.append('station_ids', id));
    }
    if (params.start_time) {
      searchParams.append('start_time', params.start_time);
    }
    if (params.end_time) {
      searchParams.append('end_time', params.end_time);
    }
    if (params.variables && params.variables.length > 0) {
      params.variables.forEach((variable) => searchParams.append('variables', variable));
    }
    if (params.limit) {
      searchParams.append('limit', params.limit.toString());
    }

    const queryString = searchParams.toString();
    const endpoint = `/v1/station-data/data${queryString ? `?${queryString}` : ''}`;

    const result = await this.makeRequest<StationDataResponse>(endpoint);

    // Fix encoding issues in station names if needed
    if (result.data && result.data.data) {
      result.data.data = result.data.data.map((dataPoint: StationDataPoint) => ({
        ...dataPoint,
        name: fixStationNameEncoding(dataPoint.name),
      }));
    }

    return result;
  }

  async getLatestStationData(
    stationId: string,
    hours: number = 24,
    variables?: string[],
  ): Promise<ApiResponse<StationDataPoint[]>> {
    const searchParams = new URLSearchParams();
    searchParams.append('hours', hours.toString());

    if (variables && variables.length > 0) {
      variables.forEach((variable) => searchParams.append('variables', variable));
    }

    const result = await this.makeRequest<StationDataPoint[]>(
      `/v1/station-data/stations/${stationId}/latest?${searchParams.toString()}`,
    );

    // Fix encoding issues in station names if needed
    if (result.data) {
      result.data = result.data.map((dataPoint: StationDataPoint) => ({
        ...dataPoint,
        name: fixStationNameEncoding(dataPoint.name),
      }));
    }

    return result;
  }
}
